import { FunctionComponent, useEffect, useRef, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ApolloError, useMutation, useQuery } from "@apollo/client";
import { PODS_DATA, GOAL_DETAILS, GET_ALL_SKILLS } from "../../api/query";
import BreadCumb from "../../components/common/Breadcrumb";
import Loader from "../../components/common/Loader";
import InputsNew from "../../components/common/InputsNew";
import DateSelector from "../../components/common/DateSelector";
import SearchableInputsDropdown from "../../components/common/SearchableDropdown";
import {
  showError,
  showSuccess,
} from "../../components/common/ToastNotification";
import backArrow from "../../assets/task/back_arrow.svg";
import aiAgenticon from "../../assets/kpi-items/profile-round-1342-svgrepo-com.svg";
import { UPDATE_POD_GOAL } from "../../api/mutation";
import InputsDropdownMultipleSearchSelect from "../../components/common/InputsDropdownMultipleSearchSelect";
import closeDowpDowniconimg from "../../assets/task/closedropdown-cicon.svg";

// TODO: confirm which field actually flags an agent slot on goal_details tasks (no role_slot here — using tag/doer as a stand-in)
const isAgentTask = (task: any) =>
  (task?.tag || "").toLowerCase().includes("agent") ||
  (task?.doer || "").toLowerCase().includes("agent");

// Checklist items with these kinds are verified by the agent, not the human — never required to be checked manually.
const isAgentChecklistItem = (kind: string) =>
  kind === "agent_lint" || kind === "agent_selfcheck";

interface TaskAssigneeState {
  employee_id: string;
  employee_name: string;
  task_start_date: string | null;
  task_end_date: string | null;
  duration_hours: string;
  duration_minutes: string;
}

interface TaskChecklistState {
  [checklistIndex: number]: boolean;
}

const EditGoalFromTemplate: FunctionComponent = () => {
  const { userInfo } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  const state = location.state as {
    goalID?: any;
    projectID?: any;
    projectName?: string;
  };

  // goal_id can arrive either via route param (/goals/:goalId/edit) or via navigation state
  const goalID = params?.goalId || state?.goalID;
  const employeeID = userInfo?.employee_id || "";

  // ── Read-only goal fields (populated from the GET call, never edited here) ──
  // NOTE: goal_details doesn't return project_id/project_name, so those still come through navigation state,
  // the same way the create-goal flow receives them today.
  const [projectID] = useState(state?.projectID || "");
  const [projectName] = useState(state?.projectName || "");
  const [goalTitle, setGoalTitle] = useState("");
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [description, setDescription] = useState("");
  const [whatBuiling, setWhatBuilding] = useState("");

  const [podId, setPodId] = useState("");
  const [selectedPodName, setSelectedPodName] = useState("");
  const [selectedTemplateName, setSelectedTemplateName] = useState("");
  const [agencyName, setAgencyName] = useState("");
  const [screenName, setScreenName] = useState("");
  const [kranumItem, setKranumItem] = useState("");
  const [selectedMembers, setSelectedMembers] = useState<
    { key: string; name: string }[]
  >([]);
    const [selectedMembersProject, setSelectedMembersProject] = useState<
    { key: string; name: string }[]
  >([]);

  // ── Editable: task assignments only ──
  const [parsedTasks, setParsedTasks] = useState<any[]>([]);
  const [taskAssignees, setTaskAssignees] = useState<{
    [taskId: string]: TaskAssigneeState;
  }>({});
  const [taskChecklists, setTaskChecklists] = useState<{
    [taskId: string]: TaskChecklistState;
  }>({});
const [taskSkills, setTaskSkills] = useState<{
  [taskId: string]: { key: string; name: string }[];
}>({});
const [taskNotes, setTaskNotes] = useState<{
  [taskId: string]: string;
}>({});

  // A task can have only one temporary duplicate at a time.
  // const [duplicatedTaskIds, setDuplicatedTaskIds] = useState<Set<string>>(
  //   new Set(),
  // );
      const [skillOptions, setSkillOptions] = useState<{ key: string; name: string }[]>([]);
          const [selectedSkills, setSelectedSkills] = useState<any[]>([]);

  const isDuplicateTask = (task: any) => Boolean(task?.isDuplicate);

const handleDuplicateTask = (sourceTask: any) => {
  const sourceKey = String(sourceTask.id);

  const duplicateId = `duplicate-${sourceKey}-${Date.now()}`;

  const duplicateTask = {
    ...sourceTask,
    id: duplicateId,
    sequence: undefined,
    isDuplicate: true,
    duplicateOf: sourceKey,
    status_name: "Not Started",
    checklist_items: (sourceTask.checklist_items || []).map((item: any) => ({
      ...item,
      ticked: false,
    })),
  };

  setParsedTasks((prev) => {
    // find the last task in the list that is either the source task
    // or an existing duplicate of it, and insert right after THAT
    let lastRelatedIndex = -1;
    prev.forEach((t: any, i: number) => {
      const tKey = String(t.id);
      const isSource = tKey === sourceKey;
      const isDuplicateOfSource =
        t.isDuplicate && String(t.duplicateOf) === sourceKey;
      if (isSource || isDuplicateOfSource) {
        lastRelatedIndex = i;
      }
    });

    if (lastRelatedIndex === -1) return prev;

    const next = [...prev];
    next.splice(lastRelatedIndex + 1, 0, duplicateTask);

    return next;
  });

  setTaskAssignees((prev) => ({
    ...prev,
    [duplicateId]: {
      employee_id: "",
      employee_name: "",
      task_start_date: null,
      task_end_date: null,
      duration_hours: "",
      duration_minutes: "",
    },
  }));

  setTaskChecklists((prev) => ({
    ...prev,
    [duplicateId]: {},
  }));

  setTaskSkills((prev) => ({
    ...prev,
    [duplicateId]: [],
  }));

  setTaskNotes((prev) => ({
    ...prev,
    [duplicateId]: "",
  }));
};

 const handleRemoveDuplicateTask = (task: any) => {
  const duplicateId = String(task.id);

  setParsedTasks((prev) =>
    prev.filter((t: any) => String(t.id) !== duplicateId)
  );

  setTaskAssignees((prev) => {
    const next = { ...prev };
    delete next[duplicateId];
    return next;
  });

  setTaskChecklists((prev) => {
    const next = { ...prev };
    delete next[duplicateId];
    return next;
  });

  setTaskSkills((prev) => {
    const next = { ...prev };
    delete next[duplicateId];
    return next;
  });
    setTaskNotes((prev) => {
    const next = { ...prev };
    delete next[duplicateId];
    return next;
  });
};

  const hydratedRef = useRef(false);

  // ── Fetch the goal — goal fields, tasks (with their checklist_items), and the goal's members ──
  const {
    data: goalData,
    loading: goalLoading,
    error: goalError,
  } = useQuery(GOAL_DETAILS, {
    variables: { goal_id: Number(goalID) },
    fetchPolicy: "no-cache",
    skip: !goalID,
  });

  // Resolve human-readable pod / template names for display only — goal_details only returns their IDs
  const { data: podsResponse } = useQuery(PODS_DATA, {
    variables: {
      agency_id: parseInt(userInfo?.agencies?.id || "0"),
      start_date: new Date().toISOString().split("T")[0],
      end_date: new Date().toISOString().split("T")[0],
    },
    fetchPolicy: "no-cache",
    skip: !userInfo?.agencies?.id,
  });

  const [updatePodGoal, { loading: updateLoading }] = useMutation(
    UPDATE_POD_GOAL,
    {
      fetchPolicy: "no-cache",
    },
    
  );

     // Skills 
      const { loading: skillLoading } = useQuery(GET_ALL_SKILLS, {
          fetchPolicy: 'no-cache',
          onCompleted:(res)=>{
            const result = res?.get_all_skills?.skills
                 const options = result?.map((skill: any) => ({
                key: skill.id,
                name: skill?.skill_name
            }));
            setSkillOptions(options);
          }
      });

  // ── Hydrate all state from the GET response, once ──
  useEffect(() => {
    if (hydratedRef.current) return;
    const goal = goalData?.goal_details?.goal_details;
    if (!goal) return;
    hydratedRef.current = true;

    setGoalTitle(goal.title || "");
    setStartDate(goal.start_date ? goal.start_date.split("T")[0] : null);
    setEndDate(goal.end_date ? goal.end_date.split("T")[0] : null);
    setDescription(goal.description || "");
    setWhatBuilding(goal.what_building || "");
    setPodId(String(goal.pod_id || ""));
    setSelectedTemplateName(goal?.defect?.template_name || goal?.template_tag  || "");
    setAgencyName(goal?.defect?.agency_name || "");
    setScreenName(goal?.defect?.screen_name || "");
    setKranumItem(goal?.defect?.ticket_id || "");

    const membersProject = (goalData?.goal_details?.project_members || [])
      .map((m: any) => ({
        key: String(m.employee_id),
        name: m.member_name,
      }))
      .sort((a: any, b: any) =>
        (a?.name || "").localeCompare(b?.name || "", undefined, {
          sensitivity: "base",
        }),
      );

    setSelectedMembersProject(membersProject);


      const members = (goalData?.goal_details?.goal_details?.goal_members || [])
      .map((m: any) => ({
        key: String(m.employee_id),
        name: m.member_name,
      }))
      .sort((a: any, b: any) =>
        (a?.name || "").localeCompare(b?.name || "", undefined, {
          sensitivity: "base",
        }),
      );

    setSelectedMembers(members);


           // Handle skills from existing goal
            if (goal?.skills?.length > 0) {
                const skillOptions = goal?.skills?.map((s: any) => ({
                    key: s?.skill_id,
                    name: s?.skill_name
                }));
                setSelectedSkills(skillOptions);
            } else {
                // Clear skills if goal has none
                setSelectedSkills([]);
            }

    const tasks = goal.tasks || [];
    setParsedTasks(tasks);

    const initialAssignees: { [key: string]: TaskAssigneeState } = {};
    const initialChecklists: { [key: string]: TaskChecklistState } = {};
const initialTaskSkills: { [key: string]: { key: string; name: string }[] } = {};
const initialTaskNotes: { [key: string]: string } = {};
    tasks.forEach((task: any) => {
      const taskKey = String(task.id);
      const assignedMember = members.find(
        (m: any) => m.key == String(task.assigned_to_id),
      );

      // end_datetime's time part looks like "02:03:00+00:00" — pull just HH and MM out of it
      const timeMatch = task.end_datetime
        ?.split("T")[1]
        ?.match(/^(\d{2}):(\d{2})/);
      // const durationHours = timeMatch ? timeMatch[1] : "";
      // const durationMinutes = timeMatch ? timeMatch[2] : "";
      const durationHours = task?.hours ? task?.hours : "00";
      const durationMinutes = task?.mins ? task?.mins : "00";

      initialAssignees[taskKey] = {
        employee_id: task.assigned_to_id ? String(task.assigned_to_id) : "",
        employee_name:
          assignedMember?.member_name || task?.assigned_to_name || "",
        task_start_date: task.start_datetime
          ? task.start_datetime.split("T")[0]
          : null,
        task_end_date: task.end_datetime
          ? task.end_datetime.split("T")[0]
          : null,
        duration_hours: durationHours,
        duration_minutes: durationMinutes,
      };
       initialTaskNotes[taskKey] = task.task_note || "";

      const checklistState: TaskChecklistState = {};
      (task.checklist_items || []).forEach((item: any, cidx: number) => {
        checklistState[cidx] = isAgentChecklistItem(item.kind)
          ? true // agent-verified items are never a human gate
          : Boolean(item.ticked);
      });
      initialChecklists[taskKey] = checklistState;
      

initialTaskSkills[taskKey] = (task.skills || []).map((s: any) => ({
  key: String(s.skill_id),
  name: s.skill_name,
}));
    });
    setTaskAssignees(initialAssignees);
    setTaskChecklists(initialChecklists);
    setTaskSkills(initialTaskSkills);
    setTaskNotes(initialTaskNotes);
  }, [goalData]);

  // Resolve pod name once pods load
  useEffect(() => {
    if (!podId || !podsResponse?.pods_data?.data) return;
    const pod = podsResponse.pods_data.data.find(
      (p: any) => String(p.pod_id) === podId,
    );
    if (pod) setSelectedPodName(pod.pod_name);
  }, [podId, podsResponse]);

  const handleTaskAssigneeChange = (
    taskKey: string,
    field: keyof TaskAssigneeState,
    value: string | null,
  ) => {
    setTaskAssignees((prev) => ({
      ...prev,
      [taskKey]: {
        ...prev[taskKey],
        [field]: value,
      } as TaskAssigneeState,
    }));
  };

const handleTaskNoteChange = (taskKey: string, value: string) => {
  setTaskNotes((prev) => ({
    ...prev,
    [taskKey]: value,
  }));
};

  // Only hours 0–99 and minutes 0–59, digits only
  const handleDurationChange = (
    taskKey: string,
    field: "duration_hours" | "duration_minutes",
    raw: string,
  ) => {
    const digitsOnly = raw.replace(/[^0-9]/g, "");
    if (digitsOnly === "") {
      handleTaskAssigneeChange(taskKey, field, "");
      return;
    }
    const num = parseInt(digitsOnly, 10);
    if (field === "duration_minutes" && num > 59) return;
    if (field === "duration_hours" && num > 24) return;
    handleTaskAssigneeChange(taskKey, field, String(num));
  };



  const handleSubmit = async () => {
    // Validate every visible task before saving.
    for (const task of parsedTasks) {
      const taskKey = String(task.id);
      const assignee = taskAssignees[taskKey];

      if (!assignee?.employee_id) {
        showError(`Please assign a member to "${task.title}"`);
        return;
      }

      if (!assignee.task_start_date) {
        showError(`Please select a start date for "${task.title}"`);
        return;
      }

      if (!assignee.task_end_date) {
        showError(`Please select an end date for "${task.title}"`);
        return;
      }

      const start = new Date(assignee.task_start_date);
      const end = new Date(assignee.task_end_date);

      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
        showError(`Please select valid dates for "${task.title}"`);
        return;
      }

      if (end < start) {
        showError(`End date cannot be before start date for "${task.title}"`);
        return;
      }

      if (startDate && start < new Date(startDate)) {
        showError(
          `Task start date for "${task.title}" cannot be before the goal start date`,
        );
        return;
      }

      const hours = Number(assignee.duration_hours || 0);
      const minutes = Number(assignee.duration_minutes || 0);

      if (!Number.isInteger(hours) || hours < 0 || hours > 24) {
        showError(`Please enter valid hours (0-24) for "${task.title}"`);
        return;
      }

      if (!Number.isInteger(minutes) || minutes < 0 || minutes > 59) {
        showError(`Please enter valid minutes (0-59) for "${task.title}"`);
        return;
      }

      console.log(task, "task");
      if (hours === 0 && minutes === 0) {
        showError(`Please enter a duration for "${task.title}"`);
        return;
      }

      // Task Note is required
      const taskNote = taskNotes[taskKey]?.trim();

      // if (!taskNote) {
      //   showError(`Please enter a task note for "${task.title}"`);
      //   return;
      // }

      const checklist_items = task.checklist_items || [];
      for (let cidx = 0; cidx < checklist_items.length; cidx++) {
        const item = checklist_items[cidx];
        if (isAgentChecklistItem(item.kind)) continue;
      }
    }

    // Formats hours/minutes into "HH:MM:00" — defaults to "00:00:00" if left blank
    const formatTaskTime = (hours: string, minutes: string) => {
      const hh = String(parseInt(hours || "0", 10)).padStart(2, "0");
      const mm = String(parseInt(minutes || "0", 10)).padStart(2, "0");
      return `${hh}:${mm}:00`;
    };



             // Validate skills
        // if (selectedSkills?.length < 1) {
        //     showError('Please select at least one goal skill');
        //     return;
        // }

            // Prepare skills
        const skillIds = selectedSkills?.length > 0 ? selectedSkills?.map((s: any) => {
            return parseInt(s?.key)
        }) : [];

    const taskUpdates = parsedTasks
      .filter((task: any) => !isDuplicateTask(task))
      .map((task: any) => {
        const taskKey = String(task.id);
        const assignee = taskAssignees[taskKey];

        const taskTime = formatTaskTime(
          assignee.duration_hours,
          assignee.duration_minutes,
        );

        const tickedChecklistIds = (task.checklist_items || [])
          .map((item: any, cidx: number) => {
            const isTicked = isAgentChecklistItem(item.kind)
              ? item.ticked
              : Boolean(taskChecklists[taskKey]?.[cidx]);

            return isTicked ? parseInt(item.id) : null;
          })
          .filter((id: number | null): id is number => id !== null);

        return {
          task_id: Number(task.id),
          employee_id: parseInt(assignee.employee_id),
          start_datetime: `${assignee.task_start_date}T00:00:00`,
          end_datetime: `${assignee.task_end_date}T${taskTime}`,
          ticked_checklist_ids: tickedChecklistIds,
           skill_ids: (taskSkills[taskKey] || []).map((s: any) => parseInt(s.key)),
              task_note: taskNotes[taskKey] ?? "",
        };
      });

    const duplicateTasks = parsedTasks
      .filter((task: any) => isDuplicateTask(task))
      .map((task: any) => {
        const taskKey = String(task.id);
        const assignee = taskAssignees[taskKey];

        const taskTime = formatTaskTime(
          assignee.duration_hours,
          assignee.duration_minutes,
        );

        const tickedChecklistIds = (task.checklist_items || [])
          .map((item: any, cidx: number) => {
            const isTicked = isAgentChecklistItem(item.kind)
              ? item.ticked
              : Boolean(taskChecklists[taskKey]?.[cidx]);

            return isTicked ? parseInt(item.id) : null;
          })
          .filter((id: number | null): id is number => id !== null);

        return {
          task_id: Number(task.duplicateOf),
          employee_id: parseInt(assignee.employee_id),
          start_datetime: `${assignee.task_start_date}T00:00:00`,
          end_datetime: `${assignee.task_end_date}T${taskTime}`,
          skill_ids: (taskSkills[taskKey] || []).map((s: any) => parseInt(s.key)),
          task_note: taskNotes[taskKey] ?? "",
        };
      });

    try {
      const res = await updatePodGoal({
        variables: {
          goal_id: parseInt(goalID),
          title: goalTitle,
          description: description,
          start_date: startDate,
          end_date: null,
          task_updates: taskUpdates,
          duplicate_tasks: duplicateTasks,
          skill_ids:skillIds,
          ...(selectedTemplateName == "Enhancement" && {
            what_building: whatBuiling,
          }),
        },
      });

      const result = res.data?.update_pod_goal;
      if (result?.status_code === 200) {
        showSuccess(result?.message || "Goal updated successfully");
        navigate(-1);
      } else {
        showError(result?.message || "Failed to update goal");
      }
    } catch (err) {
      if (err instanceof ApolloError) {
        showError(err.message);
      }
    }
  };

  if (goalError) {
    showError("Failed to load goal details");
  }

const handleSelectTaskSkill = (
  taskKey: string,
  key: any,
  name: any,
  type: any,
) => {
  if (key === "all") {
    if (name === true) {
      setTaskSkills((prev) => ({ ...prev, [taskKey]: [] }));
    } else {
      setTaskSkills((prev) => ({ ...prev, [taskKey]: selectedSkills }));
    }
  } else {
    setTaskSkills((prev) => {
      const current = prev[taskKey] || [];
      const exists = current.some((item) => item.key == key);
      const updated = exists
        ? current.filter((item) => item.key != key)
        : [...current, { key, name, type }];
      return { ...prev, [taskKey]: updated };
    });
  }
};

const handleRemoveTaskSkill = (taskKey: string, key: string) => {
  setTaskSkills((prev) => ({
    ...prev,
    [taskKey]: (prev[taskKey] || []).filter((item) => item.key !== key),
  }));
};

 const handleSelectSkills = (key: any, name: any, type: any) => {
        const allOptions = skillOptions;

        if (key === "all") {
            if (name === true) {
                // Deselect all
                setSelectedSkills([]);
            } else {
                // Select all
                setSelectedSkills(allOptions);
            }
        } else {
            setSelectedSkills((prev) => {
                const exists = prev.some((item) => item.key == key);
                if (exists) {
                    // REMOVE if already selected
                    return prev.filter((item) => item.key != key);
                } else {
                    // ADD if not selected
                    return [...prev, { key, name, type }];
                }
            });
        }
    };

      const handleRemoveSkill = (key: string) => {
        // Check if this skill is used in any NEW task (added via popup)

        // Check if this skill is used in any EXISTING task (from API)
        const isSkillUsedInExistingTasks = goalData?.goal_details?.goal_details?.tasks?.some((task: any) => {
            return task.skills?.some((skill: any) => skill.skill_id == key);
        });

        if (isSkillUsedInExistingTasks) {
            showError('This skill is being used in one or more tasks. Please remove it from tasks first.');
            return;
        }

        setSelectedSkills((prev) => prev.filter((item) => item.key !== key));
    };


  return (
    <>
      <div
        id="cts-respon-p-dv"
        className="w-full relative min-h-screen overflow-hidden block flex-row items-start justify-center flex-wrap content-start py-40 px-20 mq991:p-8 mq575:p-4 box-border text-left text-base font-manrope"
      >
        <div className="flex-1 flex flex-col items-start justify-start gap-6">
          <BreadCumb clientName={projectName} />

          <div className="self-stretch flex flex-row items-center justify-start gap-[5px] text-2xl">
            <img
              className="w-6 relative h-6 overflow-hidden shrink-0 cursor-pointer dark-white-img"
              alt=""
              src={backArrow}
              onClick={() => navigate(-1)}
            />
            <h3 className="m-0 flex-1 relative text-inherit font-bold font-inherit mq450:text-base dark:text-white">
              Edit Goal
            </h3>
          </div>

          <div className="self-stretch rounded-lg bg-surface-bg-primary dark:bg-slate-800 flex flex-col items-start justify-start gap-6 p-8 mq575:p-3">
            <div className="grid gap-4 assigntask-card-dv self-stretch">
              <div className="grid gap-4 inner-assigntask-card-dv self-stretch">
                {/* ── Goal Details — locked, populated from the GET call ── */}
                <h5 className="my-0 self-stretch text-lg dark:text-white font-bold flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-green-500 text-white text-xs flex items-center justify-center font-bold shrink-0">
                    ✓
                  </span>
                  Goal Details
                </h5>

                <div className="grid grid-cols-4 mq1275:grid-cols-3 mq950:grid-cols-2 mq575:grid-cols-1 gap-[8px] min-w-full">
                  <InputsNew
                    label="Project Name*"
                    type="text"
                    contentPlaceholder={projectName}
                    disabled={true}
                  />
                  <InputsNew
                    label="Pod*"
                    type="text"
                    contentPlaceholder={selectedPodName}
                    disabled={true}
                  />
                  <InputsNew
                    label="Goal Title*"
                    type="text"
                    contentPlaceholder={goalTitle}
                    value={goalTitle}
                    disabled={true}
                  />
                  <InputsNew
                    label="Template Tag*"
                    type="text"
                    contentPlaceholder={selectedTemplateName}
                    value={selectedTemplateName}
                    disabled={true}
                  />
                  {
                    agencyName &&                   <InputsNew
                    label="Agency Name*"
                    type="text"
                    contentPlaceholder={agencyName}
                    value={agencyName}
                    disabled={true}
                  />
                  }
                  {
                    screenName &&                   <InputsNew
                    label="Screen Name"
                    type="text"
                    contentPlaceholder={screenName}
                    value={screenName}
                    disabled={true}
                  />
                  }

                  <div className="flex flex-col items-start justify-start gap-[8px] startdate-inner-input">
                    <DateSelector
                      label="Start Date*"
                      selectedDate={startDate}
                      onChange={() => {}}
                      disabled={true}
                      nothideParsedValue={true}
                    />
                  </div>
                  <div className="flex flex-col items-start justify-start gap-[8px] startdate-inner-input">
                    <DateSelector
                      label="End Date*"
                      selectedDate={endDate}
                      onChange={() => {}}
                      disabled={true}
                      nothideParsedValue={true}
                    />
                  </div>
                  {
                    kranumItem &&                   <InputsNew
                    label="Kranum *"
                    type="text"
                    contentPlaceholder={kranumItem}
                    value={kranumItem}
                    disabled={true}
                  />
                  }

                </div>

                <div className="self-stretch">
                  <InputsNew
                    contentPlaceholder="Goal Description"
                    label="Description"
                    type="textarea"
                    value={description}
                    disabled={true}
                  />
                </div>
              
  
                {selectedTemplateName == "Enhancement" && (
                                 <div className="self-stretch enhancement-textrea-input-dv grid gap-4">

                     <div className="grid gap-1">
                      <label className="self-stretch relative font-semibold darkblue-blg-item dark:text-white" htmlFor="">What we are building — and what we are not</label>
                      <span className="relative text-sm font-body-xs-semi-bold text-colors-text-black text-left inline-block dark:text-white">
                          What we are building — and what we are not
                      </span>
                    </div>
                    <div className="self-stretch">
                    <InputsNew
                      contentPlaceholder="The Scope"
                      label="The Scope*"
                      type="textarea"
                      value={whatBuiling}
                      disabled={false}
                      onChange={(e) => {
                        setWhatBuilding(e.target.value);
                      }}
                    />
                  </div>
                   </div>
                  
                )}
            
                    {/* <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:mq1275:grid-cols-2 mq575:mq1275:grid-cols-1 gap-[8px] min-w-full">
                                            <div className="grid">
                                                <InputsDropdownMultipleSearchSelect
                                                    label="Skills"
                                                    placeHolder="Choose"
                                                    options={skillOptions}
                                                    onSelect={handleSelectSkills}
                                                    inputType="checkbox"
                                                    checkedItems={selectedSkills}
                                                />
                
                                                {selectedSkills?.length > 0 && (
                                                    <div className="reportmulselect-blg flex-1 w-full cts-flex1-remd contents flex-col items-start justify-start text-left text-sm mq825:max-w-[100%] text-text-text-primary font-body-reg min-w-full">
                                                        <div className="inner-reportmulselect-blg inner-reportmulselect-item items-start justify-start text-left text-sm mq825:max-w-[100%] text-text-text-primary font-body-reg mt-2">
                                                            {selectedSkills.map((ss: any) => (
                                                                <div
                                                                    key={ss?.key}
                                                                    className="reportmulselect-blg-dv rounded-radius-md1 overflow-hidden flex flex-row items-center justify-start gap-[8px] inner-item-select-dv border-[1px] border-solid border-surface-border dark:bg-slate-700"
                                                                >
                                                                    <div className="flex flex-row items-center justify-start">
                                                                        <div className="relative text-sm font-body-xs-semi-bold text-colors-text-black text-left inline-block dark:text-white">
                                                                            {ss?.name}
                                                                        </div>
                                                                    </div>
                                                                    <img
                                                                        className="h-4 w-4 relative overflow-hidden shrink-0 cursor-pointer"
                                                                        loading="lazy"
                                                                        alt="Remove"
                                                                        src={closeDowpDowniconimg}
                                                                        onClick={() => handleRemoveSkill(ss.key)}
                                                                    />
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div> */}
              </div>
            </div>

            <div className="grid gap-4 assigntask-card-dv self-stretch">
              <div className="grid gap-4 inner-assigntask-card-dv self-stretch">
                <div className="self-stretch flex flex-col gap-3">
                  <label className="text-sm font-semibold dark:text-white">
                    Goal Members*
                  </label>
                  <div className="grid grid-cols-4 mq1275:grid-cols-3 mq950:grid-cols-2 mq575:grid-cols-1 gap-2">
                    {selectedMembers.map((m) => (
                      <div
                        key={m.key}
                        className="flex items-center gap-2 p-3 rounded-md member-list-item bg-gray-50 dark:bg-slate-700 opacity-70"
                      >
                        <img src={aiAgenticon} className="w-3 h-3" alt="" />
                        <span className="text-sm dark:text-white truncate flex-1">
                          {m.name}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="self-stretch border-t border-gray-200 dark:border-gray-700" />

            {/* ── Assign Tasks — the only editable section ── */}
            <div className="grid gap-4 assigntask-card-dv self-stretch">
              <div className="grid gap-4 inner-assigntask-card-dv self-stretch">
                <div className="grid gap-2">
                  <h5 className="my-0 self-stretch text-lg dark:text-white font-bold flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-surface-primary-surface text-white text-xs flex items-center justify-center font-bold shrink-0">
                      ✎
                    </span>
                    Assign Tasks
                  </h5>
                  <p className="my-0 text-sm text-gray-500 dark:text-gray-400">
                    Update the assignee, dates, duration, and checklist for each
                    task. All checklist items must be completed to save changes.
                  </p>
                </div>

                <div className="self-stretch grid gap-4">
                  {parsedTasks.map((task: any, idx: number) => {
                    const isAgent = isAgentTask(task);
                    const taskKey = String(task.id);

                    const taskName = String(task.task || task.title || "")
                      .trim()
                      .toLowerCase();

                    const sameTaskNameCount = parsedTasks.filter(
                      (t: any) =>
                        String(t.task || t.title || "")
                          .trim()
                          .toLowerCase() === taskName && !isDuplicateTask(t),
                    ).length;

                    // const canDuplicate = sameTaskNameCount === 1;

                    const assignee = taskAssignees[taskKey];
                    return (
                      <div
                        key={taskKey}
                        className={`p-4 rounded-lg border ${
                          isAgent
                            ? "border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/10"
                            : "border-[1px] border-solid border-surface-border inner-assigntask-card-dv"
                        }`}
                      >
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-7 h-7 rounded-full bg-gray-100 dark:bg-slate-700  border-[1px] border-solid border-surface-border text-[#2b2b2b] dark:text-white text-xs flex items-center justify-center flex-shrink-0 font-bold">
                            {idx + 1}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-semibold dark:text-white">
                                {task.title}
                              </span>
                              {/* {isAgent && (
                                <span className="inline-flex items-center gap-1 text-xs bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300 px-2 py-0.5 rounded font-medium">
                                  🤖 Agent Slot
                                </span>
                              )} */}
                            </div>
                          </div>

                          <div className="flex items-center gap-2 shrink-0">
                         {isDuplicateTask(task) ? (
  <button
    type="button"
    aria-label="Remove duplicated task"
    title="Remove duplicated task"
    onClick={() => handleRemoveDuplicateTask(task)}
    className="text-gray-400 cta-red transition mt-0.5 ml-4 flex-shrink-0 pt-1"
  > <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                           
    
  </button>
) : (
  <button
    type="button"
    aria-label={`Duplicate task ${task.task}`}
    title="Duplicate task"
    onClick={() => handleDuplicateTask(task)}
    className="cursor-pointer flex flex-col items-start justify-start p-[10px] plus-task-btn taskdash-plush-img rounded"
  >
     <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="16"
                                  height="16"
                                  viewBox="0 0 16 16"
                                  fill="none"
                                >
                                  <path
                                    d="M8 3.5V12.5"
                                    stroke="#fff"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                  />
                                  <path
                                    d="M12.5 8H3.5"
                                    stroke="#fff"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                  />
                                </svg>
  </button>
)}
                          </div>
                        </div>

                        <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:grid-cols-2 mq575:grid-cols-1 mt-[8px] gap-[8px] min-w-full">
                          <SearchableInputsDropdown
                            disableDropdown={task?.status_name == "Closed"}
                            key={`assign-${taskKey}`}
                            label="Assign To"
                            initialValue={assignee?.employee_name || ""}
                            placeHolder="Select member"
                            options={selectedMembersProject.map((m) => ({
                              key: m.key,
                              name: m.name,
                            }))}
                            dontselectlastValue={true}
                            onSelect={(key: string, name: string) => {
                              handleTaskAssigneeChange(
                                taskKey,
                                "employee_id",
                                key,
                              );
                              handleTaskAssigneeChange(
                                taskKey,
                                "employee_name",
                                name,
                              );
                            }}
                          />
                          <DateSelector
                            disabled={task?.status_name == "Closed"}
                            key={`start-${taskKey}`}
                            label="Start Date"
                            selectedDate={assignee?.task_start_date || null}
                            onChange={(date: string | null) =>
                              handleTaskAssigneeChange(
                                taskKey,
                                "task_start_date",
                                date,
                              )
                            }
                            minDate={
                              startDate ? new Date(startDate) : undefined
                            }
                            nothideParsedValue={true}
                          />
                          <DateSelector
                            disabled={task?.status_name == "Closed"}
                            key={`end-${taskKey}`}
                            label="End Date"
                            selectedDate={assignee?.task_end_date || null}
                            onChange={(date: string | null) => {
                              if (!date) {
                                handleTaskAssigneeChange(
                                  taskKey,
                                  "task_end_date",
                                  null
                                );
                                return;
                              }

                              const startDateValue = assignee?.task_start_date;

                              if (startDateValue && date !== startDateValue) {
                                showError(
                                  `End Date must be the same as Start Date for "${task.title}"`
                                );
                                return;
                              }

                              handleTaskAssigneeChange(
                                taskKey,
                                "task_end_date",
                                date
                              );
                            }}
                            minDate={
                              assignee?.task_start_date
                                ? new Date(assignee.task_start_date)
                                : startDate
                                  ? new Date(startDate)
                                  : undefined
                            }
                            nothideParsedValue={true}
                          />

                          <div className="flex flex-col gap-[8px] duration-input-item-dv">
                            <label className="text-xs font-semibold dark:text-white">
                              Duration
                            </label>
                            <div className="flex items-center gap-2">
                              <div className="py-2.5 cursor-pointer w-12 self-stretch shadow-[0px_1px_2px_rgba(16,_24,_40,_0.05)] h-[21px] tabelin-input-dv rounded-radius-md bg-text-text-tertiary dark:text-white dark:bg-slate-700 overflow-hidden flex flex-row items-center justify-start px-[15px] border-[1px] border-solid  border-surface-border ">
                                <input
                                  disabled={task?.status_name == "Closed"}
                                  type="text"
                                  inputMode="numeric"
                                  maxLength={2}
                                  placeholder="HH"
                                  value={assignee?.duration_hours ?? ""}
                                  onChange={(e) =>
                                    handleDurationChange(
                                      taskKey,
                                      "duration_hours",
                                      e.target.value,
                                    )
                                  }
                                  className="w-full border-none outline-none focus-none text-center text-sm  dark:text-white bg-dark-item"
                                />
                              </div>
                              {/* <span className="text-xs text-gray-500 dark:text-gray-400">
                                Hrs
                              </span> */}
                              <div className="py-2.5 cursor-pointer w-12 self-stretch shadow-[0px_1px_2px_rgba(16,_24,_40,_0.05)] h-[21px] tabelin-input-dv rounded-radius-md bg-text-text-tertiary dark:text-white dark:bg-slate-700 overflow-hidden flex flex-row items-center justify-start px-[15px] border-[1px] border-solid  border-surface-border ">
                                <input
                                  disabled={task?.status_name == "Closed"}
                                  type="text"
                                  inputMode="numeric"
                                  maxLength={2}
                                  placeholder="MM"
                                  value={assignee?.duration_minutes ?? ""}
                                  onChange={(e) =>
                                    handleDurationChange(
                                      taskKey,
                                      "duration_minutes",
                                      e.target.value,
                                    )
                                  }
                                  className="w-full border-none outline-none focus-none text-center text-sm  dark:text-white bg-dark-item"
                                />
                              </div>
                              {/* <span className="text-xs text-gray-500 dark:text-gray-400">
                                Min
                              </span> */}
                            </div>
                          </div>
                        </div>

{/* <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-600">
  <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:grid-cols-2 mq575:grid-cols-1 gap-[8px] min-w-full">
    <div className="grid">
      <InputsDropdownMultipleSearchSelect
        label="Skills"
        placeHolder="Choose"
        options={selectedSkills}
        onSelect={(key: any, name: any, type: any) =>
          handleSelectTaskSkill(taskKey, key, name, type)
        }
        inputType="checkbox"
        checkedItems={taskSkills[taskKey] || []}
        disableDropdown={task?.status_name == "Closed"}
      />

      {(taskSkills[taskKey]?.length ?? 0) > 0 && (
        <div className="reportmulselect-blg flex-1 w-full cts-flex1-remd contents flex-col items-start justify-start text-left text-sm mq825:max-w-[100%] text-text-text-primary font-body-reg min-w-full">
          <div className="inner-reportmulselect-blg inner-reportmulselect-item items-start justify-start text-left text-sm mq825:max-w-[100%] text-text-text-primary font-body-reg mt-2">
            {taskSkills[taskKey].map((ss: any) => (
              <div
                key={ss?.key}
                className="reportmulselect-blg-dv rounded-radius-md1 overflow-hidden flex flex-row items-center justify-start gap-[8px] inner-item-select-dv border-[1px] border-solid border-surface-border dark:bg-slate-700"
              >
                <div className="flex flex-row items-center justify-start">
                  <div className="relative text-sm font-body-xs-semi-bold text-colors-text-black text-left inline-block dark:text-white">
                    {ss?.name}
                  </div>
                </div>
                <img
                  className="h-4 w-4 relative overflow-hidden shrink-0 cursor-pointer"
                  loading="lazy"
                  alt="Remove"
                  src={closeDowpDowniconimg}
                  onClick={() => handleRemoveTaskSkill(taskKey, ss.key)}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  </div>
</div> */}
<div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-600">
  <label className="text-xs font-semibold dark:text-white mb-1 block">
    Task Description*
  </label>
  <textarea
    disabled={task?.status_name == "Closed"}
    value={taskNotes[taskKey] ?? ""}
    onChange={(e) => handleTaskNoteChange(taskKey, e.target.value)}
    placeholder="Add a description for this task..."
    rows={2}
    className="w-full text-sm rounded-radius-md border-[1px] border-solid border-surface-border p-2 dark:bg-slate-700 dark:text-white outline-none"
  />
</div>
                        {task.checklist_items?.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-600">
                            <p className="my-0 text-xs font-semibold dark:text-white mb-2">
                              Checklist:
                            </p>
                            <div className="grid gap-1">
                              {task.checklist_items.map(
                                (item: any, cidx: number) => {
                                  const isAgentCheck = isAgentChecklistItem(
                                    item.kind,
                                  );
                                  return (
                                    <label
                                      key={item.id ?? cidx}
                                      className="flex items-center gap-2 cursor-pointer py-[5px] px-[5px]  border-[1px] border-solid border-surface-border"
                                    >
                                      {isAgentCheck ? (
                                        <span className="text-xs flex-shrink-0 agent-bg-dv">
                                          Agent
                                        </span>
                                      ) : (
                                        <span className="text-xs flex-shrink-0 human-bg-dv">
                                          Human
                                        </span>
                                      )}
                                      <span
                                        className={`text-xs text-gray-700 dark:text-gray-300`}
                                      >
                                        {item.checklist_name}
                                      </span>
                                      {isAgentCheck && (
                                        <span className="text-xs text-[#2B2B2B] italic">
                                          (agent verifies)
                                        </span>
                                      )}
                                    </label>
                                  );
                                },
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div className="self-stretch flex flex-row items-center justify-end gap-[8px]">
                  <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="cursor-pointer bg-white py-2.5 h-[44px] px-[42px] btn-text-purple rounded-radiuscomponent-button box-border flex flex-row items-center justify-center gap-[12px] border-[2px] border-solid border-surface-primary-surface hover:bg-gainsboro-100"
                  >
                    <span className="relative text-sm font-body-xs-semi-bold  dark:text-white">
                      Cancel
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={updateLoading || goalLoading}
                    className="cursor-pointer [border:none] py-2.5 h-[44px] px-[42px] bg-surface-primary-surface rounded-radiuscomponent-button flex flex-row items-center justify-center gap-[12px] hover:bg-dodgerblue disabled:opacity-50"
                  >
                    <span className="relative text-sm font-body-xs-semi-bold text-surface-bg-primary">
                      Update Goal
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {(goalLoading || updateLoading || skillLoading) && <Loader />}
    </>
  );
};

export default EditGoalFromTemplate;
